import { useAppSelector } from 'hooks/redux';
import type { List as ImmutableList } from 'immutable';
//  CORE
import { DateTime } from 'luxon';
import React, { Fragment, useMemo, useState } from 'react';
// import { useDispatch } from 'react-redux';

// DATA
import type { a2aTransfersTypes } from 'app-components/flux/a2aTransfers';
import { a2aTransfersSelectors, a2aTransfersUtils } from 'app-components/flux/a2aTransfers';
// import { accountsSelectors } from 'app-components/flux/accounts';
// import { transactionsSelectors, transactionsTypes } from 'app-components/flux/transactions';
// import * as fundingAccountsActions from 'data/fundingAccounts/actions';
// import * as fundingAccountsSelectors from 'data/fundingAccounts/selectors';
import { getLogger } from 'app-components/utils/core';
import { intervalForRange } from 'app-components/utils/date';

// MUI
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';

//  LOCAL
import QDateRangePicker from 'components/QDateRangePicker';
import VerifiedFundingAccountDropdown from 'components/SimplifiSelects/VerifiedFundingAccountDropdown';
import ZeroStateView from 'components/ZeroStateView';

import TransferLine from './TransferLine';

const log = getLogger('containers/Settings/ChildPages/TransferHistory/TranfserList');

const useStyles = makeStyles()((theme) => ({
  root: {
    minWidth: 540,
    minHeight: 700,
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'flex-start',
    paddingBottom: 20,
    flexDirection: 'column',
    '&.READY': {
      height: '100%',
      flexGrow: 1,
      flexShrink: 2,
      flexBasis: '720px',
    },
  },
  initiation: {
    paddingTop: 8,
  },
  largeHeaderText: {
    marginTop: 30,
  },
  readyContainer: {
    width: '100%',
    paddingLeft: 24,
    paddingRight: 24,
  },
  selectionHeader: {
    display: 'flex',
    width: '100%',
    padding: 24,
    paddingBottom: 16,
    borderBottom: `1px solid ${theme.palette.border.light}`,
    gap: 12,
  },
  zeroStyles: {
    justifyContent: 'center',
    width: '100%',
    display: 'flex',
    marginTop: 64,
  },
}));

// Ranges to include in the datepicker (Custom is automatically there by default)
const dateRanges = ['Last 30 days', 'Last 3 months', 'Last 6 months', 'Last year', 'This year', 'All time'];

const TransferList = () => {
  // const dispatch = useDispatch();
  const { classes, cx } = useStyles();

  //  ========= SELECTORS ==========

  // const hasTransfers = useAppSelector(a2aTransfersSelectors.getHasTransfers);
  const transferList: ImmutableList<a2aTransfersTypes.A2aTransfer> = useAppSelector(
    a2aTransfersSelectors.getTransferList,
  );

  const [selectedAccounts, setSelectedAccounts] = useState<{ name: string; id: string }[]>([]);
  const selectedIds = useMemo(() => selectedAccounts.map((item) => item.id), [selectedAccounts]);

  const [doNotFilter, setDoNotFilter] = useState<boolean>(true);

  //   const accountList = useSelector(accountsSelectors.getAccountsById);

  //   const view = useLoadState(transactionsSelectors);
  //   const accountView = useLoadState(accountsSelectors);
  //   const fundingView = useLoadState(fundingAccountsSelectors);
  //   const profileView = useLoadState(profileSelectors);

  const [dateRange, setDateRange] = useState('Last 3 months');

  // shortcuts in a memo so that values are refreshed on component mount
  const shortcuts = useMemo(
    () =>
      dateRanges.map((item) => {
        const { start, end } = intervalForRange({ value: item }, DateTime.local());
        return {
          id: item,
          label: item,
          getValue: () => [start, end],
        };
      }),
    [],
  );

  const [start, end] = useMemo(() => {
    const values = shortcuts.find((option) => option.id === dateRange)?.getValue?.() || [];
    return [
      (values[0] || DateTime.local().minus({ months: 3 })).toISODate(),
      (values[1] || DateTime.local()).toISODate(),
    ];
  }, [shortcuts, dateRange]);

  const noTransfersView = useMemo(
    () => (
      <Box className={classes.zeroStyles}>
        <ZeroStateView
          size='small'
          id='transfer-zero-state'
          title='No transfers found'
          description='Try selecting a different date range'
          qIllustration='boxEmptyDisabled'
        />
      </Box>
    ),
    [classes],
  );

  const filteredTransfers = useMemo(
    () =>
      transferList.filter((transfer) => {
        const accountGood =
          doNotFilter ||
          selectedIds.includes(transfer.sourceFundingAccount?.id) ||
          selectedIds.includes(transfer.destinationFundingAccount?.id);

        const transferStart = (
          transfer?.execution?.startedOn ||
          transfer?.schedule?.startOn ||
          transfer?.execution?.completedOn ||
          transfer?.execution?.cancelledOn ||
          transfer?.execution?.failedOn ||
          ''
        )?.slice(0, 10);
        const transferEnd = (
          transfer?.execution?.completedOn ||
          transfer?.execution?.failedOn ||
          transfer?.execution?.cancelledOn ||
          transfer?.schedule?.completeOn ||
          transfer?.schedule?.startOn ||
          ''
        )?.slice(0, 10);

        return (
          accountGood &&
          (dateRange === 'All time' ||
            (transferStart <= end && transferStart >= start) ||
            (transferEnd >= start && transferEnd <= end) ||
            // TODO: We need IFS to change the meaning of startedOn field to be when it was first SCHEDULED not when it first started processing
            transfer.status === a2aTransfersUtils.A2A_TRANSFER_ENUMS.SCHEDULED)
        );
      }),
    [doNotFilter, selectedIds, transferList, start, end, dateRange],
  );

  const [pendingGroupedList, completedGroupList] = a2aTransfersSelectors.getTransferHistory(filteredTransfers);

  // console.log('dateRange', dateRange);

  // ViewStates
  // const tooFewAccounts = fundingAccountsByAccountId?.size < 2 && !A2ATransferTxns.size; //  If there is a transfer history, we should not hide the page just because the user has no more verified accounts left

  //   const showSkeletonView =
  //     loadState.skeletonStates.includes(view) ||
  //     loadState.skeletonStates.includes(accountView) ||
  //     loadState.skeletonStates.includes(fundingView) ||
  //     loadState.skeletonStates.includes(profileView);
  //   const showTryAgainView =
  //     loadState.tryAgainStates.includes(view) ||
  //     loadState.tryAgainStates.includes(accountView) ||
  //     loadState.tryAgainStates.includes(fundingView) ||
  //     loadState.tryAgainStates.includes(profileView) ||
  //     loadState.updateRequiredStates.includes(view) ||
  //     loadState.updateRequiredStates.includes(accountView) ||
  //     loadState.updateRequiredStates.includes(fundingView) ||
  //     loadState.updateRequiredStates.includes(profileView);

  //   //  Only having the history (view) be ready to show matters, if all accounts are deleted, history still exists regardless, and we ought to show it.
  //   const showNoDataView = loadState.noDataStates.includes(view) && !tooFewAccounts;
  //   const showNoAccountsView = tooFewAccounts;
  //   const showReadyView = loadState.readyStates.includes(view) && !tooFewAccounts;

  //  FAILED takes priority over EMPTY as there is a bug that causes one of the selectors to be marked as EMPTY when reloading the
  //  page actually fixes it and populates it with data.
  //   let mainView = '';
  //   if (showReadyView) {
  //     mainView = 'READY';
  //   } else if (showTryAgainView) {
  //     mainView = 'FAILED';
  //   } else if (showNoDataView) {
  //     mainView = 'EMPTY';
  //   } else if (showNoAccountsView) {
  //     mainView = 'NO_ACCOUNTS';
  //   } else if (showSkeletonView) {
  //     mainView = 'LOADING';
  //   }
  const mainView = 'READY';

  const sortedPendingList = useMemo(
    () =>
      pendingGroupedList
        .map((transferList, createdDate) => (
          <Fragment key={createdDate}>
            <Typography variant='captionRegular' className={classes.initiation}>
              Initiated {DateTime.fromISO(createdDate).toFormat('LLL dd')}
            </Typography>
            <br />
            {transferList
              .sortBy((transfer) => -DateTime.fromISO(a2aTransfersUtils.getDate(transfer)).toMillis())
              .map((transfer, index) => (
                <TransferLine id={`pending-transfer-${index}`} key={transfer.id} transfer={transfer} index={index} />
              ))
              .toIndexedSeq()}
          </Fragment>
        ))
        .toIndexedSeq(),
    [classes.initiation, pendingGroupedList],
  );

  const sortedCompletedList = useMemo(
    () =>
      completedGroupList
        .map((transferList, createdDate) => (
          <Fragment key={createdDate}>
            <Typography variant='captionRegular' className={classes.initiation}>
              Initiated {DateTime.fromISO(createdDate).toFormat('LLL dd')}
            </Typography>
            <br />
            {transferList
              .sortBy((transfer) => -DateTime.fromISO(a2aTransfersUtils.getDate(transfer)).toMillis())
              .map((transfer, index) => (
                <TransferLine id={`completed-transfer-${index}`} key={transfer.id} transfer={transfer} index={index} />
              ))
              .toIndexedSeq()}
          </Fragment>
        ))
        .toIndexedSeq(),
    [classes.initiation, completedGroupList],
  );

  return (
    <Paper className={cx(classes.root, mainView)}>
      {/* {mainView === 'FAILED' && failedToFetch}

      {mainView === 'LOADING' && <div className={classes.skeletons}>{tableSkeleton}</div>}

      {mainView === 'EMPTY' && zeroState()}

      {mainView === 'NO_ACCOUNTS' && noAccountsState} */}

      {mainView === 'READY' && (
        <>
          <div className={classes.selectionHeader}>
            <VerifiedFundingAccountDropdown
              sortBy='TYPE'
              showAllItemsMenu
              selectedItems={selectedAccounts}
              onSelected={setSelectedAccounts}
              setIsAllSelected={setDoNotFilter}
            />
            <QDateRangePicker
              id='transfer-date-picker'
              range
              compact={dateRange === 'All time'}
              selectedRangeId={dateRange}
              ranges={shortcuts}
              dateRange={dateRange}
              onSelect={(value: any) => {
                // TODO: TS QDateRangePicker
                const { start, end, selectedRangeId } = value;
                if (selectedRangeId) {
                  setDateRange(selectedRangeId);
                } else if (start && end) {
                  log.warn('Missing date range!');
                  setDateRange('All time');
                }
              }}
            />
          </div>

          <div className={classes.readyContainer}>
            {pendingGroupedList?.size ? (
              <>
                <Typography className={classes.largeHeaderText} variant='titleMedium'>
                  In Progress
                </Typography>

                {sortedPendingList}
              </>
            ) : null}

            {completedGroupList?.size ? (
              <>
                <Typography className={classes.largeHeaderText} variant='titleMedium'>
                  Completed
                </Typography>

                {sortedCompletedList}
              </>
            ) : null}
            {!(completedGroupList?.size || pendingGroupedList?.size) ? noTransfersView : null}

            {/* <Typography
                className={classes.largeHeaderText}
                variant='titleMedium'
            >
                Active
            </Typography> */}
            {/* {sortedFilteredTransferHistory(completedTransfers).length ||
          sortedFilteredTransferHistory(pendingTransfers).length ? (
            <>
              {sortedFilteredTransferHistory(pendingTransfers).length ? (
                <Table style={{ marginTop: 15 }}>
                  <TableBody className={classes.tableBod}>{renderBody(pendingTransfers)}</TableBody>
                </Table>
              ) : null}

              {sortedFilteredTransferHistory(completedTransfers).length ? (
                <Typography className={classes.largeHeaderText} variant='titleMedium'>
                  Completed
                </Typography>
              ) : null}

              {sortedFilteredTransferHistory(completedTransfers).length ? (
                <Table style={{ marginTop: 15 }}>
                  <TableBody className={classes.tableBod}>{renderBody(completedTransfers)}</TableBody>
                </Table>
              ) : null}
            </>
          ) : (
            zeroState(true)
          )} */}
          </div>
        </>
      )}
    </Paper>
  );
};

TransferList.displayName = 'TransferList';

export default TransferList;
